/**
 * Sound when the AI hands a chat to me (owner 26 ก.ย. 69: "ด้วยแต่ปิดได้" — docs/ai-mode.md).
 *
 * - A short, soft two-note chime synthesised with the Web Audio API (no audio file, nothing hosted elsewhere).
 * - "เสียงเตือน" on/off per person, remembered per browser (localStorage `cf-chat.sound.handoff`, default on).
 * - Never twice for the same hand-off: played keys are remembered (localStorage, 24 h), so a socket reconnect or a
 *   page refresh that shows the same hand-off again stays quiet. Several hand-offs at once = one chime.
 * - Browser autoplay rules: the AudioContext is created/resumed on the first click or key press; before that (or when
 *   the browser refuses) nothing plays and nothing fails.
 * Every storage access is wrapped: private windows / blocked storage just fall back to the defaults.
 */

export const SOUND_KEY = 'cf-chat.sound.handoff'
export const PLAYED_KEY = 'cf-chat.sound.played'
export const PLAYED_KEEP_MS = 24 * 3600 * 1000
export const PLAYED_MAX = 200
/** Hand-offs closer together than this share one chime. */
export const BURST_MS = 2500

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>

function storage(): StorageLike | null {
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}

function read(key: string, s: StorageLike | null = storage()): string | null {
  try {
    return s ? s.getItem(key) : null
  } catch {
    return null
  }
}

function write(key: string, value: string, s: StorageLike | null = storage()): void {
  try {
    s?.setItem(key, value)
  } catch {
    /* private window / quota: the setting just is not remembered */
  }
}

// ---------------------------------------------------------------- on / off

/** Default ON; only an explicit "off" turns it off. */
export function soundEnabled(s: StorageLike | null = storage()): boolean {
  return read(SOUND_KEY, s) !== 'off'
}

export function setSoundEnabled(on: boolean, s: StorageLike | null = storage()): void {
  write(SOUND_KEY, on ? 'on' : 'off', s)
}

// ---------------------------------------------------------------- once per hand-off

/**
 * Remembers which hand-offs already chimed (key = "<conversation id>:<session id>").
 * `claim(key)` → 'play' (first time, not in a burst) · 'burst' (first time, but a chime just played) · 'seen'.
 */
export class PlayedLog {
  private lastPlayAt = -Infinity

  constructor(
    private readonly s: StorageLike | null = storage(),
    private readonly now: () => number = () => Date.now(),
  ) {}

  private load(): Record<string, number> {
    const raw = read(PLAYED_KEY, this.s)
    if (!raw) return {}
    try {
      const v = JSON.parse(raw) as unknown
      if (!v || typeof v !== 'object' || Array.isArray(v)) return {}
      const out: Record<string, number> = {}
      for (const [k, t] of Object.entries(v as Record<string, unknown>)) if (typeof t === 'number') out[k] = t
      return out
    } catch {
      return {}
    }
  }

  private save(map: Record<string, number>): void {
    const t = this.now()
    const kept = Object.entries(map)
      .filter(([, at]) => t - at < PLAYED_KEEP_MS)
      .sort((a, b) => b[1] - a[1])
      .slice(0, PLAYED_MAX)
    write(PLAYED_KEY, JSON.stringify(Object.fromEntries(kept)), this.s)
  }

  has(key: string): boolean {
    const at = this.load()[key]
    return at !== undefined && this.now() - at < PLAYED_KEEP_MS
  }

  /** Remember a hand-off without ringing (sound switched off): it must not ring later either. */
  mark(key: string): void {
    if (this.has(key)) return
    const map = this.load()
    map[key] = this.now()
    this.save(map)
  }

  claim(key: string): 'play' | 'burst' | 'seen' {
    if (this.has(key)) return 'seen'
    const map = this.load()
    const t = this.now()
    map[key] = t
    this.save(map)
    if (t - this.lastPlayAt < BURST_MS) return 'burst'
    this.lastPlayAt = t
    return 'play'
  }
}

// ---------------------------------------------------------------- the sound

interface AudioParamLike {
  setValueAtTime(v: number, t: number): unknown
  linearRampToValueAtTime(v: number, t: number): unknown
  exponentialRampToValueAtTime(v: number, t: number): unknown
}
interface NodeLike {
  connect(n: unknown): unknown
}
export interface AudioContextLike {
  readonly state: string
  readonly currentTime: number
  readonly destination: unknown
  resume(): Promise<void>
  createOscillator(): NodeLike & { type: string; frequency: AudioParamLike; start(t: number): void; stop(t: number): void }
  createGain(): NodeLike & { gain: AudioParamLike }
}

/** Two soft notes, a major sixth apart (E5 → C#6), bell-like decay, quiet. About 0.9 s. */
export const NOTES: { hz: number; at: number; len: number; peak: number }[] = [
  { hz: 659.25, at: 0, len: 0.55, peak: 0.12 },
  { hz: 1108.73, at: 0.16, len: 0.75, peak: 0.1 },
]

export class Chime {
  private ctx: AudioContextLike | null = null
  private unlocked = false

  constructor(private readonly make: () => AudioContextLike | null = defaultContext) {}

  /** Call from a user gesture (first click / key press): browsers only allow audio after one. */
  unlock(): void {
    try {
      if (!this.ctx) this.ctx = this.make()
      if (this.ctx && this.ctx.state !== 'running') void this.ctx.resume().catch(() => undefined)
      this.unlocked = !!this.ctx
    } catch {
      this.ctx = null
    }
  }

  get ready(): boolean {
    return this.unlocked && this.ctx?.state === 'running'
  }

  /** true = the chime was scheduled. Never throws. */
  play(): boolean {
    const ctx = this.ctx
    if (!ctx || !this.unlocked) return false
    try {
      if (ctx.state !== 'running') {
        void ctx.resume().catch(() => undefined)
        if (ctx.state !== 'running') return false
      }
      const t0 = ctx.currentTime + 0.02
      for (const n of NOTES) {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.setValueAtTime(n.hz, t0 + n.at)
        gain.gain.setValueAtTime(0.0001, t0 + n.at)
        gain.gain.linearRampToValueAtTime(n.peak, t0 + n.at + 0.012) // soft attack, no click
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + n.at + n.len) // bell-like fade
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start(t0 + n.at)
        osc.stop(t0 + n.at + n.len + 0.05)
      }
      return true
    } catch {
      return false
    }
  }
}

function defaultContext(): AudioContextLike | null {
  const w = globalThis as unknown as { AudioContext?: new () => AudioContextLike; webkitAudioContext?: new () => AudioContextLike }
  const Ctor = w.AudioContext ?? w.webkitAudioContext
  return Ctor ? new Ctor() : null
}

/** One chime for the whole page, unlocked by the first click / key press anywhere. */
export const chime = new Chime()
let unlockInstalled = false
export function installUnlock(target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> | null =
  typeof window !== 'undefined' ? window : null): void {
  if (unlockInstalled || !target) return
  unlockInstalled = true
  const once = (): void => {
    chime.unlock()
    target.removeEventListener('pointerdown', once)
    target.removeEventListener('keydown', once)
  }
  target.addEventListener('pointerdown', once)
  target.addEventListener('keydown', once)
}
